import { ImageName } from "@mcleod/images";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import {
    Button,
    ChangeEvent,
    ClickEvent,
    Component,
    DataSource,
    DataSourceMode,
    EditRowDecorator,
    Image,
    KeyEvent,
    Label,
    LabelProps,
    Layout,
    LayoutProps,
    Overlay,
    Panel,
    SelectionEvent,
    SlideoutDecorator,
    Snackbar,
    Tab,
    Table,
    TableRow,
    Textbox,
    ValidationResult
} from "@mcleod/components";
import {
    Alignment, Api, CurrencyUtil, DatePart, DateRange, DateUtil, DisplayType, HorizontalAlignment, Keys, LogManager,
    ModelRow, Navigation, StringUtil, VerticalAlignment
} from "@mcleod/core";
import { getDetentionControlBoolean, getDispatchControlBoolean, getDispatchControlValue, LMEGeneralSettings } from "@mcleod/general";
import { OrderTabView } from "../OrderTabView";
import { CommodityQuickInfo } from "../CommodityQuickInfo";
import { MultiStop } from "../MultiStop";
import { ComponentListenerDefs } from "@mcleod/components/src/base/ComponentListenerDefs";
import { PostToLoadBoards } from "../PostToLoadBoards";
import { ModelOrders } from "../models/ModelOrders";
import {
    getLateImageTooltip, getTimeDiff, setRowComponentVisible
} from "@mcleod/powerbroker/src/BrokeragePlanningUtil";
import { HoursOfService } from "../asset/HoursOfService";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { DriverAlerts, DriverAlertsProps } from "./DriverAlerts";
import { DispatchEquipAlerts } from "../asset/DispatchEquipAlerts";
import { ShipmentStatusSlideout } from "../ShipmentStatusSlideout";
import { DetentionUtil } from "../DetentionUtil";
import { AppointmentUtil } from "../AppointmentUtil";

const log = LogManager.getLogger("lme.dispatch.PlanningUtil");
const SHIPMENT_STATUS = "S";

interface EditComponents {
    textbox: Textbox;
    editButton: Button;
    saveButton: Button;
    cancelButton: Button;
}

interface TextboxEditabilityProps {
    textbox: Textbox;
    idText: string;
    tableRow: TableRow;
    onSave: () => Promise<void>;
    useWrapper?: boolean;
    noWrapperRowBreak?: boolean;
}

export enum TempWarnings {
    EXCEEDS,
    APPROACHING,
    WITHIN
}

// START - Regulator types
export enum ExpirationValid {
    WARN,
    ERROR
}

export type RegulatoryValidation = {
    valid: ExpirationValid,
    diff: number,
    alwaysWarn?: boolean,
}

export type DriverRegulatoryValidationResults = {
    driverLicense?: RegulatoryValidation,
    driverPhysical?: RegulatoryValidation,
    driverMVR?: RegulatoryValidation,
}
export type TractorTrailerRegulatoryValidationResults = {
    tractorInspect?: RegulatoryValidation,
    trailerInspect?: RegulatoryValidation,
    trailer2Inspect?: RegulatoryValidation,
    trailer3Inspect?: RegulatoryValidation,
}
// END - Regulatory types

interface IntercompanyProps {
    orderId: string,
    moveId: string,
    customerId: string,
}
type DetentionItem = {
    image?: Image | [Image, Image, Image];
    field?: string;
    color?: string;
}
export type DetentionItems = {
    origin?: DetentionItem;
    destination?: DetentionItem;
    next_stop?: DetentionItem;
    statuses?: DetentionItem;
}


export class PlanningUtil {

    public static isLateWarning(row: ModelRow, stop: "origin" | "next_stop" | "destination"): boolean {
        if (row.get(`${stop}_actual_arrival`) != null || row.isNull(`${stop}_stop_id`)) return false;

        const diff = this.getTimeDiff(new Date(this.getStopTime(row, stop)));
        if (diff == null) return false;
        const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
        const isPickupWarning = stop === "origin" || (stop === "next_stop" && row.get("next_stop_move_sequence") == 1);
        const warning = isPickupWarning ? dispatchControl.pickup_warning : dispatchControl.delivery_warning;
        return diff < (warning * 60 * 60 * 1000)
    }

    /**
     * Returns the stop's scheduled arrival time as a string with the timezone.
     * @param row The TableRow the data is in.
     * @param stop Determines if this is for the origin or destination.
     * @returns
     */
    public static getStopTime(row: ModelRow, stop: "origin" | "next_stop" | "destination"): string {
        if (row == null)
            return null;
        else if (stop == "next_stop" && row.get("next_stop_early") === undefined) {
            stop = "origin";
        }

        let time = row.get(`${stop}_late`);
        if (time == null)
            time = row.get(`${stop}_early`);

        if (StringUtil.isEmptyString(time))
            return null;

        const tz = row.get(`${stop}_timezone`);
        return time + (tz != null ? (" " + tz) : "");
    }

    public static checkScheduledLate(tableRow: TableRow, stop: "origin" | "next_stop" | "destination") {
        let panelDate: Panel;
        if (stop === "origin") {
            panelDate = tableRow.findComponentById("panelOriginDate") as Panel;
        } else if (stop === "next_stop") {
            panelDate = tableRow.findComponentById("panelNextStopWindow") as Panel;
        } else {
            panelDate = tableRow.findComponentById("panelDestDate") as Panel;
        }

        if (panelDate == null)
            return;

        const isOrigin = "origin" === stop;

        const stopDisplayText = isOrigin ? "pickup" : (stop === "next_stop" ? "next stop" : "delivery");
        const stopLabelText = isOrigin ? "Pickup" : (stop === "next_stop" ? "Next Stop" : "Deliver");

        const apptTimeDiff = getApptTimeDiff(tableRow, stop, panelDate);
        const isApptLate = apptTimeDiff < 0;
        const isApptApproaching = apptTimeDiff != null;
        panelDate.tooltip = isApptLate ?
        `This movement's ${stopDisplayText} time has passed` : isApptApproaching ? getLateImageTooltip(apptTimeDiff, stopLabelText) : null;

        panelDate.components.forEach(component => {
            component.color = isApptLate ? "error" : isApptApproaching ? "warning" : ""
            component.fontBold = isApptLate || isApptApproaching;
        });

        function getApptTimeDiff(tableRow: TableRow, stop: "origin" | "next_stop" | "destination", comp: Component): number {
            const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
            const isPickupWarning = stop === "origin" ||
                           (stop === "next_stop" && tableRow.data?.get("next_stop_move_sequence") == 1);
            const warning = isPickupWarning ? dispatchControl.pickup_warning : dispatchControl.delivery_warning;

            const diff = getTimeDiff(new Date(PlanningUtil.getStopTime(tableRow?.data, stop)));

            if (diff == null || (diff > (warning * 60 * 60 * 1000)) || (tableRow.data.get(`${stop}_actual_arrival`) != null)) return null;
            return diff;
        }
    }

    public static checkAppointmentRequiredConfirmed(tableRow: TableRow) {
        setAppointmentRequiredConfirmed(tableRow, "origin");
        setAppointmentRequiredConfirmed(tableRow, "destination");
        AppointmentUtil.setIntermediateAppointmentImageAndLabel({
            tableRow: tableRow,
            imageRequiredId: "imageApptRequiredCount",
            labelRequiredId: "labelApptRequiredCount",
            requiredField: "appt_required_count",
            imageConfirmedId: "imageApptConfirmedCount",
            labelConfirmedId: "labelApptConfirmedCount",
            confirmedField: "appt_confirmed_count",
            createClickListener: true,
        })

        function setAppointmentRequiredConfirmed(tableRow: TableRow, stop: "origin" | "destination") {
            const compAppointmentRequired = tableRow.findComponentById(stop === "origin" ? "imageOriginAppointment" : "imageDestAppointment") as Image;
            if (compAppointmentRequired == null) return;

            const isApptRequired = tableRow.data.getBoolean(`${stop}_appt_required`, false);
            const isApptConfirmed = tableRow.data.getBoolean(`${stop}_appt_confirmed`, false);
            compAppointmentRequired.visible = isApptRequired;
            compAppointmentRequired.color = isApptConfirmed ? "success" : "caution";
            compAppointmentRequired.name = isApptConfirmed ? ImageName.appointmentConfirmed : ImageName.appointmentRequired;
            compAppointmentRequired.tooltip = isApptConfirmed ? "Appointment Confirmed" : "Appointment Required";
        }
    }

    /**
     * Returns the difference in time from the given time to the current time (relative to user's timezone).
     * Returned number is in milliseconds.
     * If the returned value is negative, it means the given time has passed.
     * @param date The time to compare against.
     * @returns
     */
    public static getTimeDiff(date: Date): number {
        return date.getTime() - new Date().getTime();
    }

    public static navigateToOrder(event: ClickEvent) {
        const orderId = (event.target as Label).text;
        if (!StringUtil.isEmptyString(orderId) && orderId != "--") {
            this.navigateToOrderId(orderId);
        }
    }

    public static navigateToOrderId(orderId: string) {
        OrderTabView.navigateTo({key: orderId, newTab: true});
    }

    public static navigateToManifest(manifestId: string) {
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + manifestId, { newTab: true });
    }

    public static navigateToMovement(movementId: string, orderId?: string) {
        if (StringUtil.isEmptyString(movementId))
            return;
        OrderTabView.navigateTo({key: movementId, orderId: orderId ?? "none", newTab: true});
    }

    public static tabSelectionEvent(event: SelectionEvent) {
        if (event.newSelection instanceof Tab) {
            const table = event.newSelection.findComponentById((component: Component) => component.id?.startsWith("tableMovements") === true) as Table;
            if (table != null) {
                table.scrollToSelection();
                table.setRowsExpanded(false);
            }
        }
    }

    public static populateTable(table: Table) {
        // the following will populate up to 5 rows (only called when hidden)
        // This avoids populating them when the user switches tab
        const rowsToPopulate = Math.min(5, table.rowCount)
        for (let i = 0; i < rowsToPopulate; i++)
            table.rows[i]?.populateDOMIfNeeded();
    }

    // START - Planning Comment stuff
    public static setupOrderComments(tableRow: TableRow) {
        const textbox = tableRow.findComponentById("textboxPlanningComment") as Textbox;
        const onSave = async () => {
            const data = tableRow.data as ModelRow;
            const orderId: string = data.get("order_id");
            await Api.post("lme/dispatch/update-order-planning-comment",
                { order_id: orderId, planning_comment: textbox.text });
        }

        this.addTextboxEditability({
            textbox: textbox,
            idText: "PlanningComment",
            tableRow: tableRow,
            onSave: onSave
        });
    }

    public static setupTractorComments(tableRow: TableRow) {
        const textbox = tableRow.findComponentById("textboxPlanningComment") as Textbox;
        const onSave = async () => {
            const data = tableRow.data as ModelRow;
            const tractorId: string = data.get("tractor.id");
            await Api.post("lme/dispatch/update-tractor-planning-comment",
                { tractor_id: tractorId, planning_comment: textbox.text });
        }

        this.addTextboxEditability({
            textbox: textbox,
            idText: "PlanningComment",
            tableRow: tableRow,
            onSave: onSave
        });
    }

    public static buttonEditCommentOnClickListener(event: ClickEvent) {
        const editButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(editButton);
        const planningComment = this.getPlanningCommentTextbox(tableRow);
        const saveButton: Button = this.getPlanningCommentSaveButton(tableRow);
        const cancelButton: Button = this.getPlanningCommentCancelButton(tableRow);
        editButton["originalText"] = planningComment.text;
        planningComment.printable = false;
        planningComment.enabled = true;
        planningComment.focus();
        saveButton.visible = true;
        cancelButton.visible = true;
        editButton.visible = false;
    }

    public static async savePlanningCommentOnKeyDown(event: KeyEvent, saveButton: Button) {
        if (event.ctrlKey && event.key === Keys.ENTER) {
            if (saveButton.visible) {
                saveButton.clicked();
            }
        }
    }

    public static async buttonSaveCommentOnClickListener(event: ClickEvent) {
        const saveButton = event.target as Button;
        saveButton.busy = true;
        const tableRow = TableRow.getContainingTableRow(saveButton);
        const planningComment = this.getPlanningCommentTextbox(tableRow);
        planningComment.enabled = false;
        const editButton: Button = this.getPlanningCommentEditButton(tableRow);
        const cancelButton: Button = this.getPlanningCommentCancelButton(tableRow);
        const orderId: string = tableRow.data.get("order_id");
        await Api.post("lme/dispatch/update-order-planning-comment",
            { order_id: orderId, planning_comment: planningComment.text })
            .catch(err => {
                log.error("Error saving comment", err);
                planningComment.enabled = true;
                planningComment.focus();
                Snackbar.showWarningSnackbar("Error saving planning comment: " + err.message);
            })
            .finally(() => saveButton.busy = false);
        planningComment.printable = true;
        saveButton.visible = false;
        cancelButton.visible = false;
        editButton.visible = true;
    }

    public static buttonCancelCommentOnClickListener(event: ClickEvent) {
        const cancelButton = event.target as Button;
        const tableRow = TableRow.getContainingTableRow(cancelButton);
        const planningComment = this.getPlanningCommentTextbox(tableRow);
        const editButton: Button = this.getPlanningCommentEditButton(tableRow);
        const saveButton: Button = this.getPlanningCommentSaveButton(tableRow);
        planningComment.text = editButton["originalText"];
        planningComment.enabled = false;
        planningComment.printable = true;
        saveButton.visible = false;
        cancelButton.visible = false;
        editButton.visible = true;
    }

    public static textboxPlanningCommentOnChangeListener(event: ChangeEvent) {
        if (event.newValue.length <= 80) return;
        const textbox: Textbox = event.target as Textbox;
        textbox.showAdhocTooltip("You have exceeded the 80 character limit.", {
            position: Alignment.RIGHT,
            shaking: true,
            timeout: 5000
        });
        if (event.oldValue.length <= 80) {
            textbox.text = event.oldValue;
        } else {
            textbox.text = null;
        }
    }

    public static textboxPlanningCommentOnClickListener(event: ClickEvent) {
        const planningComment = event.target as Textbox;
        if (planningComment.printable !== true && document.activeElement === planningComment.getEventTarget())
            event.stopPropagation();
    }

    public static textboxPlanningCommentOnDblClickListener = PlanningUtil.textboxPlanningCommentOnClickListener;

    private static getPlanningCommentTextbox(tableRow: TableRow): Textbox {
        return tableRow.findComponentById("textboxPlanningComment") as Textbox;
    }

    private static getPlanningCommentEditButton(tableRow: TableRow): Button {
        return tableRow.findComponentById("buttonEditComment") as Button;
    }

    private static getPlanningCommentSaveButton(tableRow: TableRow): Button {
        return tableRow.findComponentById("buttonSaveComment") as Button;
    }

    private static getPlanningCommentCancelButton(tableRow: TableRow): Button {
        return tableRow.findComponentById("buttonCancelComment") as Button;
    }

    public static setPlanningCommentsIcon(tableRow: TableRow, modelRow: ModelRow, driverTwo: boolean = true) {
        const commentsIcon = tableRow.findComponentById("imageComments") as Image;
        const d2Results = driverTwo ? modelRow.isNull("driver2_planning_comment") : true;
        commentsIcon.tooltip = commentsIcon.tooltipDynamicContent = null;
        if (modelRow.isNull("driver1_planning_comment") && d2Results
            && modelRow.isNull("tractor_planning_comment")
            && modelRow.isNull("trailer_planning_comment")
            && modelRow.isNull("trailer2_planning_comment")
            && modelRow.isNull("trailer3_planning_comment")
            && modelRow.isNull("planning_comment")) {
            commentsIcon.name = ImageName.add;
            commentsIcon.tooltip = "Add Planning Comment(s)";
        } else {
            commentsIcon.name = ImageName.notes;
            CommonTooltips.setTooltipContentLayout(
                commentsIcon,
                modelRow,
                "lme/dispatch/planning/PlanningCommentsQuickInfo"
            );
        }
    }

    // On real-time updates, syncs planning comments between rows with the same order id.
    // Created to solve a problem with Y-Splits where planning comments were not being updated for all rows.
    // [!] This fix is temporary until a better solution is implemented.
    public static syncOrderPlanningComments(currentRow: ModelRow, source: DataSource, table: Table) {
        if (!currentRow) return;

        const originStopType = currentRow.get("origin_stop_type", "");
        const destStopType = currentRow.get("destination_stop_type", "");
        if ("SP" != originStopType || "SD" != destStopType) return;

        const orderId = currentRow.get("order_id");
        const newPlanningComment = currentRow.get("planning_comment", "");
        source.data.forEach(row => {
            if (!(row.get("order_id") === orderId
                && row.get("planning_comment") !== newPlanningComment)) {
                return;
            }

            row.set("planning_comment", newPlanningComment);
            const tableRow = table.findTableRow(row);
            if (tableRow && tableRow.populatedDOM) {
                PlanningUtil.setPlanningCommentsIcon(tableRow, row);
            }
        });
    }
    // END - Planning Comment stuff

    public static setupDriverPta(tableRow: TableRow, driver: number) {
        const driverNum = driver.toString();
        const onSave = async () => {
            const data = tableRow.data as ModelRow;
            const driverId: string = data.get(`driver${driverNum}_id`);
            await Api.post("lme/dispatch/asset/update-driver-pta",
                {driver_id: driverId, pta_date: data.getDateTime(`driver${driverNum}_pta`), is_manual: true});
        }

        const data = tableRow.data as ModelRow;
        const textbox = tableRow.findComponentById(`textboxD${driverNum}Pta`) as Textbox;
        if (!data.isNull(`driver${driverNum}_id`)) {
            const idText = `D${driverNum}Pta`;
            if (data.get(`driver${driverNum}_record_pta`) === "Y"
                    || ((getDispatchControlBoolean("record_pta", false) || getDispatchControlBoolean("record_pta_web", false))
                    && data.get(`driver${driverNum}_record_pta`) === "U")) {
                this.addTextboxEditability({
                    textbox: textbox,
                    idText: idText,
                    tableRow: tableRow,
                    onSave: onSave,
                    useWrapper: true
                });
            }
            else {
                const buttonPanel = tableRow.findComponentById(`panel${idText}Buttons`);
                if (buttonPanel) {
                    buttonPanel.visible = false;
                }
            }
        } else {
            if (textbox) {
                textbox.visible = false;
            }
        }
    }

    // START - Editability stuff
    public static addTextboxEditability(props: TextboxEditabilityProps) {
        if (props.tableRow && props.tableRow.findComponentById(`panel${props.idText}Buttons`) != null) return;

        if (props.textbox) {
            props.textbox.addClickListener(this.editTextboxOnClick);
            props.textbox.addDblClickListener(this.editTextboxOnDblClick);
            const parent = props.textbox.parent;
            // Some components like the multiline text have issues so we don't use the wrapper panel for that
            // but for some componenets this edibility is being added to are in a column with other components
            // and adding the wrapper keeps the edit buttons with that component
            if (props.useWrapper) {
                props.textbox.rowBreak = false;
                const panel = this.getWrapperPanel(props.idText);
                panel.add(props.textbox);
                panel.add(this.getButtonPanel(props.idText, props.textbox, props.onSave));
                panel.rowBreak = props.noWrapperRowBreak;
                parent?.replace(props.textbox, panel);
            } else {
                if (parent?.findComponentById(`panel${props.idText}Buttons`) != null) return;
                const panel = this.getButtonPanel(props.idText, props.textbox, props.onSave);
                parent?.add(panel);
            }
        }
    }

    private static getWrapperPanel(idText: string): Panel {
        const id = `panel${idText}`;
        return new Panel({
            id: id,
            fillRow: true,
            margin: 0,
            padding: 0,
            wrap: false
        })
    }

    private static editTextboxOnClick(event: ClickEvent) {
        const textbox = event.target as Textbox;
        if (textbox.printable !== true && document.activeElement === textbox.getEventTarget())
            event.stopPropagation();
    }

    private static editTextboxOnDblClick(event: ClickEvent) {
        const textbox = event.target as Textbox;
        if (textbox.printable !== true && document.activeElement === textbox.getEventTarget())
            event.stopPropagation();
    }

    private static getButtonPanel(idText: string, textbox: Textbox, onSave: () => Promise<void>): Panel {
        const id = `panel${idText}Buttons`;
        const hasCaption = textbox.captionVisible && !StringUtil.isEmptyString(textbox.caption);
        const marginTop = hasCaption ? 14 : 0;
        const buttonPanel = new Panel({
            align: HorizontalAlignment.RIGHT,
            fillHeight: true,
            fillRow: true,
            id: id,
            margin: 0,
            marginLeft: 4,
            maxWidth: 20,
            marginTop: marginTop,
            verticalAlign: VerticalAlignment.TOP,
            wrap: false
        });

        const editButton = this.getEditButton(idText);
        const saveButton = this.getSaveCancelButton(idText, true);
        const cancelButton = this.getSaveCancelButton(idText, false);

        const textboxWasPrintable = textbox.printable;
        const editComponents: EditComponents = {textbox, editButton, saveButton, cancelButton};
        editButton.addClickListener((event: ClickEvent) => this.buttonEditOnClick(editComponents));
        saveButton.addClickListener((event: ClickEvent) => this.buttonSaveOnClick(editComponents, onSave, textboxWasPrintable));
        cancelButton.addClickListener((event: ClickEvent) => this.buttonCancelOnClick(editComponents, textboxWasPrintable));
        buttonPanel.add(editButton, saveButton, cancelButton);

        return buttonPanel;
    }

    private static getEditButton(idText: string): Button {
        const id = `buttonEdit${idText}`;
        return new Button({
            borderWidth: 0,
            color: "primary",
            id: id,
            imageName: ImageName.pencil,
            margin: 0,
            padding: 0,
            paddingTop: 5,
            paddingBottom: 5,
            width: 20,
            height: 20,
            tooltip: "Edit"
        });
    }

    private static getSaveCancelButton(idText: string, isSave: boolean): Button {
        const id = isSave ? `buttonSave${idText}` : `buttonCancel${idText}`;
        const imageName = isSave ? ImageName.disk : ImageName.x;
        const tooltip = isSave ? "Save" : "Cancel";
        return new Button({
            borderWidth: 0,
            color: "primary",
            id: id,
            imageName,
            margin: 0,
            padding: 0,
            paddingTop: 5,
            paddingBottom: 5,
            width: 20,
            height: 20,
            tooltip: tooltip,
            visible: false
        });
    }

    public static buttonEditOnClick(components: EditComponents) {
        const {textbox, editButton, saveButton, cancelButton} = components;
        editButton["originalText"] = textbox.text;
        textbox.printable = false;
        textbox.enabled = true;
        textbox.focus();
        saveButton.visible = true;
        cancelButton.visible = true;
        editButton.visible = false;
    }

    public static async buttonSaveOnClick(components: EditComponents, onSave: () => Promise<void>, textboxWasPrintable: boolean) {
        const {textbox, editButton, saveButton, cancelButton} = components;
        saveButton.busy = true;
        await onSave().finally(() => saveButton.busy = false);
        this.updateComponentsAfterSave(textbox, saveButton, editButton, cancelButton, textboxWasPrintable);
    }

    private static updateComponentsAfterSave(textbox: Textbox, saveButton: Button, editButton: Button, cancelButton: Button, textboxWasPrintable: boolean) {
        textbox.enabled = false;
        textbox.printable = textboxWasPrintable;
        saveButton.visible = false;
        cancelButton.visible = false;
        editButton.visible = true;
    }

    public static buttonCancelOnClick(components: EditComponents, textboxWasPrintable: boolean) {
        const {textbox, editButton, saveButton, cancelButton} = components;
        textbox.text = editButton["originalText"];
        textbox.enabled = false;
        textbox.printable = textboxWasPrintable;
        saveButton.visible = false;
        cancelButton.visible = false;
        editButton.visible = true;
    }

    // END - Editability stuff

    public static setPlanningTooltipContent(comp: Component, modelRow: ModelRow) {
        if (comp.id == null) return;
        switch (comp.id) {
            case "labelLastLocation":
                comp.tooltipDynamicContent = () => modelRow.get("last_location_date");
                return;
            case "labelLastLoc":
                comp.tooltipDynamicContent = () => modelRow.get("last_location");
                return;
            case "textboxEtaToNextStop":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                    { caption: "Move Distance: ", displayType: DisplayType.STRING, text: modelRow.get("distance"), rowBreak: true, fillRow: true }
                ]);
             case "textboxEtaToPickup":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                    { caption: "Move Distance: ", displayType: DisplayType.STRING, text: modelRow.get("distance"), rowBreak: true, fillRow: true }
                ]);
            case "labelDestLocName":
            case "labelDestCityStateZip":
            case "panelDestLoc":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelOriginLocName":
            case "labelOriginCityStateZip":
            case "panelOriginLoc":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "panelNextLoc":
            case "panelEquipNextLoc":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("next_stop_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "panelFinalPreassCityStateZip":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("fp_dest_location_id"), "lme/dispatch/LocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelTotalCharges":
                return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                    {
                        caption: "Freight Charge",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("freight_charge"))
                    },
                    {
                        caption: "Other Charges",
                        displayType: DisplayType.CURRENCY,
                        text: CurrencyUtil.formatCurrency(modelRow.get("other_charge_total"))
                    }]
                );
            case "textboxPlanningComment":
            case "labelPlanningComment":
                comp.tooltipDynamicContent = () => modelRow.get("planning_comment");
                return;
            case "textboxBOL":
                comp.tooltipDynamicContent = () => modelRow.get("order_bol");
                return;
            case "labelDriverOne":
            case "labelDriverName":
            case "labelDriver1Name":
            case "textboxDriver1Name":
            case "labelEquipDriver":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver1_id"), "lme/dispatch/asset/DriverQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelEquipDriver2":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver2_id"), "lme/dispatch/asset/DriverQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "textboxD1Pta":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver1_id"), "lme/dispatch/asset/DriverPtaQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelDriver2Name":
            case "textboxDriver2Name":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("driver2_id"), "lme/dispatch/asset/DriverQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelTractor":
            case "textboxTractor":
            case "labelEquipTractor":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("tractor"), "lme/dispatch/asset/TractorQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "textboxTrailer":
            case "labelTrailer":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("trailer"), "lme/dispatch/asset/TrailerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelTrailer2":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("trailer2"), "lme/dispatch/asset/TrailerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelTrailer3":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("trailer3"), "lme/dispatch/asset/TrailerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelEquipTrailer":
                return CommonTooltips.setTooltipContentLayout(comp, this.createTrailerModelRow(modelRow, 1), "lme/dispatch/asset/TrailerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelEquipTrailer2":
                return CommonTooltips.setTooltipContentLayout(comp, this.createTrailerModelRow(modelRow, 2), "lme/dispatch/asset/TrailerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelEquipTrailer3":
                return CommonTooltips.setTooltipContentLayout(comp, this.createTrailerModelRow(modelRow, 3), "lme/dispatch/asset/TrailerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelDolly1":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("dolly1"), "lme/dispatch/asset/DollyQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelDolly2":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("dolly2"), "lme/dispatch/asset/DollyQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "textboxCommodity":
            case "panelCommodity":
                return this.setCommodityTooltipContent(comp, modelRow, {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelCustomerName":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("customer_id"), "lme/ar/CustomerQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "panelPickupCityState":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_location_id"), "lme/dispatch/LocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "panelDelCityState":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_location_id"), "lme/dispatch/LocationQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "labelTrailerTypeDescr":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("trailer_type"), "lme/dispatch/TrailerTypeQuickInfo", {
                    minWidth: 128,
                    minHeight: 140
                });
            case "imageDriverEvents":
            case "imageD1Events":
                CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/asset/DriverEventsQuickInfo",
                    {width: 1000},
                    {
                        "driver.id": modelRow.get("driver1_id"),
                        "notify_start_date": "<=t",
                        "notify_end_date": ">=t"
                    }
                );
                comp.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "imageD2Events":
                CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/asset/DriverEventsQuickInfo",
                    {width: 1000},
                    {
                        "driver.id": modelRow.get("driver2_id"),
                        "notify_start_date": "<=t",
                        "notify_end_date": ">=t"
                    }
                );
                comp.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "imageDriverManagementEvents":
                CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/asset/DriverEventsQuickInfo",
                    {width: 1000},
                    {
                        "driver.id": modelRow.get("driver1_id"),
                        "end_date": ">=t-30"
                    }
                );
                comp.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "labelDriverManagementClosestEvent":
                if (comp.getDataValue() != null && comp.getDataValue() != "--") {
                    CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/asset/DriverEventsQuickInfo",
                        {width: 1000},
                        {
                            "driver.id": modelRow.get("driver1_id"),
                            "end_date": ">=t-30"
                        }
                    );
                    comp.tooltipDynamicOptions = { position: Alignment.LEFT };
                }
                return;
            case "imageReefer":
                return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("order_id"), "lme/dispatch/asset/ReeferTempQuickInfo", {
                    minWidth: 128,
                    minHeight: 100
                });
            case "panelHoursOfService":
            case "textboxDriverAvlHrsToday":
            case "textboxDriverDutyStatus":
            case "labelDriverAvlHrsToday":
            case "labelDriverAvlHrsTomorrow":
                if (!modelRow.isNull("driver1_id")) {
                    CommonTooltips.setTooltipContentLayout(comp,
                        modelRow.getString("driver1_id"), "lme/dispatch/asset/DriverHosQuickInfo");
                }
                return;
            case "labelDriver2AvlHrsToday":
            case "labelDriver2AvlHrsTomorrow":
                if (!modelRow.isNull("driver2_id")) {
                    CommonTooltips.setTooltipContentLayout(comp,
                        modelRow.getString("driver2_id"), "lme/dispatch/asset/DriverHosQuickInfo");
                }
                return;
            case "labelTempStatus":
                return CommonTooltips.setTooltipContentLayout(
                    comp, null, "lme/dispatch/asset/DriverEquipStatusCodeQuickInfo", null,
                    {"code":modelRow.getString("driver1_op_status")}
                );
            case "labelD2TempStatus":
                return CommonTooltips.setTooltipContentLayout(
                    comp, null, "lme/dispatch/asset/DriverEquipStatusCodeQuickInfo", null,
                    {"code":modelRow.getString("driver2_op_status")}
                );
            case "buttonFuelCards":
                return this.setDriverFuelCardsTooltipFromLayoutCallback(comp, modelRow);
            case "imageLinked":
                return CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/planning/LinkedMovesQuickInfoTable"
                    , null, { linked_id: modelRow.get("linked_id"), selected_movement_id: modelRow.get("movement_id") }
                );
            case "imageServiceIncidents":
                return CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/ServiceIncidentQuickInfo"
                    , { maxWidth: 800, maxHeight: 600 },  { movement_id: modelRow.get("movement_id"), order_id: modelRow.get("order_id"), "status": "A" }
                );
        }
    }

    public static setCommodityTooltipContent(component: Component, data: ModelRow, layoutProps?: Partial<LayoutProps>) {
        component.tooltipDynamicContent = async () => {
            const commodityId = data?.get("commodity_id");
            if (StringUtil.isEmptyString(commodityId)) {
                return null;
            }
            const layout = await Layout.getLoadedLayout("lme/dispatch/CommodityQuickInfo", layoutProps) as CommodityQuickInfo;
            await layout.mainDataSource.search({ id: commodityId });
            if (!data.isNull("order_id")) {
                await layout.sourceEquipMatchDetail.search({
                    _parent_link: {
                        model: "lme/dispatch/movement",
                        "orders.id": data.get("order_id")
                    }
                });
                layout.panelLoadRequirements.visible = layout.sourceEquipMatchDetail.data.length > 0;
            }
            return layout;
        };
    }

    private static createTrailerModelRow(modelRow: ModelRow, trailerNumber: number = 1): ModelRow {
        const suffix = trailerNumber === 1 ? "" : trailerNumber.toString();
        const trailerField = `trailer${suffix}`;
        if (modelRow.isNull(trailerField)) {
            return null;
        }
        const ownershipField = `trailer${suffix}_ownership`;
        const reeferField = trailerNumber === 1 ? "reefer_id" : `reefer${suffix}_id`;
        const trailerTypeField = `trailer${suffix}_type`;
        const trailerTypeDescrField = `trailer${suffix}_type_descr`;
        const serviceStatus = modelRow.get(trailerNumber === 1 ? "trailer_active" : `trailer${suffix}_active`);
        const trailerEmpty = modelRow.get(trailerNumber === 1 ? "trailer_empty" : `trailer${suffix}_empty`) || "N";
        const trailerDropped = modelRow.get(trailerNumber === 1 ? "trailer_dropped" : `trailer${suffix}_dropped`) ? "Yes" : "No";

        const serviceStatusMap = { "A": "Active", "I": "Inactive", "S": "In Shop" };
        const emptyMap = { "N": "No", "Y": "Yes" };
        const ownershipMap = { "O": "Owned", "B": "Borrowed" };

        const trailerData = {
            id: modelRow.get(trailerField),
            trailer_dropped: trailerDropped,
            is_active: serviceStatus,
            empty: trailerEmpty,
            reefer_id: modelRow.get(reeferField),
            ownership: modelRow.get(ownershipField),
            trailer_type: modelRow.get(trailerTypeField),
            _lookup_is_active: {
                id: serviceStatus,
                descr: serviceStatusMap[serviceStatus] || serviceStatus
            },
            _lookup_empty: {
                id: trailerEmpty,
                descr: emptyMap[trailerEmpty] || trailerEmpty
            },
            _lookup_trailer_type: {
                id: modelRow.get(trailerTypeField),
                descr: modelRow.get(trailerTypeDescrField)
            },
            _lookup_ownership: {
                id: modelRow.get(ownershipField),
                descr: ownershipMap[modelRow.get(ownershipField)] || modelRow.get(ownershipField)
            },
            _lookup_reefer_id: {
                id: modelRow.get(reeferField)
            }
        };

        return new ModelRow("lme/dispatch/asset/trailer-lookup", false, trailerData);
    }

    public static setLinkStyles(label: Label) {
        label.applyLinkStyle();
    }

    public static async showMultiStopSlideout(target: Label|Button|Image, hideStopInfo = false, orderId: string = null,
        onSaveCallback?: () => void) {
        target.busy = true;
        if (!await Layout.canAccessLayout("lme/dispatch/MultiStop")) {
            target.busy = false;
            CommonDialogs.showError("Access denied to lme/dispatch/MultiStop due to permissions");
            return;
        }
        const tableRow: TableRow = TableRow.getContainingTableRow(target);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;
        let multiStopLayout: MultiStop;
        const order = orderId ?? row.get("order_id");
        const isManifest = row.get("manifest_id") != null;
        const isBrokered: boolean = row.get("brokerage") ?? false;
        const searchInput = isManifest ? { manifest_id: row.get("manifest_id"), movement_id: row.get("movement_id") } : { id: order };
        const erd = new EditRowDecorator({
            title: isManifest ? `Stop Details - Manifest ${row.get("manifest_id")}` : `Stop Details - Order ${order}`,
            layout: multiStopLayout = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop,
            layoutLoadListeners: async (event) => {
                if (isManifest) {
                    multiStopLayout.mainDataSource = multiStopLayout.sourceManifestMultistop;
                    multiStopLayout.sourceStop.parentDataSource = multiStopLayout.sourceManifestMultistop;
                    multiStopLayout.sourceStop.orderBy = [{ field: "movement_sequence", sort: "asc" }];
                    multiStopLayout.isManifest = true;
                }
                await multiStopLayout.mainDataSource.search(searchInput);
                multiStopLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                // Commodity Quick Info
                multiStopLayout.activeRow.set("order_id", order);
                const summaryData = multiStopLayout.mainDataSource.summaryData;
                this.setCommodityTooltipContent(multiStopLayout.textboxCommodityId, multiStopLayout.activeRow);
                if (hideStopInfo) {
                    multiStopLayout.panelStopInfo.visible = false;
                    multiStopLayout.panelWeight.borderTopRightRadius = 6;
                }
                if (isManifest) {
                    multiStopLayout.panelOrderInfo.visible = false;
                    multiStopLayout.panelManifestInfo.visible = true;
                    multiStopLayout.panelManifestStopInfo.visible = isBrokered;
                }
                if (isManifest && summaryData?.get("customers").length === 1) {
                    multiStopLayout.labelManifestCustAddress1.visible = true;
                    multiStopLayout.citystateManifestCust.visible = true;
                }
            },
            width: '80%',
            overlayProps: {closeOnClickOff: false, greyedBackground: true},
            onSave: async () => {
                await multiStopLayout.postChanges();
                if (onSaveCallback) {
                    onSaveCallback();
                }
            },
            onClose: (cancelled: boolean) => multiStopLayout.slideOut(),
            doAfterSlideIn: () => target.busy = false,
            onValidate: () => {
                const validationResults:ValidationResult[] = multiStopLayout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
                return validationResults;
            }
        });
    }

    public static showHoursOfServiceSlideout(event:ClickEvent, driverField:string) {
        const label = event.target as Label;
        label.busy = true;
        const tableRow: TableRow = TableRow.getContainingTableRow(label);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;
        let hosLayout: HoursOfService;
        const erd = new EditRowDecorator({
            title: 'Hours of Service',
            layout: hosLayout = Layout.getLayout("lme/dispatch/asset/HoursOfService") as HoursOfService,
            layoutLoadListeners: async (event) => {
                const driverID = row.get(driverField);
                await Promise.all([
                    hosLayout.mainDataSource.search({id: driverID}),
                    hosLayout.sourceDriverHours.search({driver_id: driverID})
                ]).then(_ => {
                    hosLayout.updateRemainingHours();
                });
                hosLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                hosLayout.sourceDriverHours.mode = DataSourceMode.UPDATE;
                hosLayout.saveButton = erd.multiButton;
            },
            width: 900,
            overlayProps: {greyedBackground: true},
            onSave: async () => {
                const validationResults:ValidationResult[] = hosLayout.validate(true, true);
                if (ValidationUtil.checkValidationResults(validationResults)) {
                    await hosLayout.sourceDriverHours.post()
                    const posts = hosLayout.sourceDriverHours.data.filter(row => row.hasChanged()).map(async row => {
                        row._appending = false;
                        await row.post()
                    });
                    await Promise.all(posts)
                } else {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
            },
            onClose: (cancelled: boolean) => hosLayout.slideOut(),
            doAfterSlideIn: () => label.busy = false
        });
    }

    public static configureTimeSinceDelivered(component: Label, value: number) {
        const labelTimeSinceDelivered = component;
        if (labelTimeSinceDelivered && value != null) {
            const hoursSinceDelivered: number = value;
            const days = Math.floor(hoursSinceDelivered / 24);
            const daySuffix = days > 1 ? "days" : "day";
            const dayComponent: string = `${days > 0 ? `${days} ${daySuffix}` : ""}`;
            const hours = hoursSinceDelivered % 24;
            const hourSuffix = hours != 1 ? "hours" : "hour";
            const hourComponent: string = `${hours} ${hourSuffix}`;

            labelTimeSinceDelivered.text = (!StringUtil.isEmptyString(dayComponent))
                ? `${dayComponent}, ${hourComponent}`
                : hourComponent;
        }
    }

    static displayIcons(tableRow: TableRow) {
        this.displayRateConImage(tableRow.findComponentById("imageRateCon") as Image, tableRow.data);
        this.displayLockImage(tableRow.findComponentById("imageLock") as Image, tableRow.data);
        this.displayLoadBoardImage(tableRow);
        this.displayDigitalOfferImage(tableRow);
        this.displayServiceFailureImage(tableRow);
        this.displayPackageErrorImage(tableRow);
        this.displayBillingImage(tableRow);
        this.displayIntercompanyImage(tableRow);
        this.displayDetentionIcon(tableRow);
    }


    static displayDetentionIcon(tableRowOrModelRow: TableRow | ModelRow, detentionItems?: DetentionItems) {
        const displayDetention = getDetentionControlBoolean("is_active", false);
        if (!displayDetention) return;
        let modelRow: ModelRow;

        if (tableRowOrModelRow instanceof ModelRow) {
            if (detentionItems == null) {
                log.error("PlanningUtil.displayDetentionIcon() was called with a ModelRow but no DetentionItems were " +
                    "provided. DetentionItems with at least 1 image field is required when calling with a ModelRow.");
                return;
            }
            modelRow = tableRowOrModelRow;
        } else {
            modelRow = tableRowOrModelRow.data as ModelRow;
            detentionItems ??= this.getDefaultDetentionItems(tableRowOrModelRow)
        }

        DetentionUtil.checkStopDetention(modelRow, detentionItems.origin?.field, detentionItems.origin?.image as Image, detentionItems.origin?.color);
        DetentionUtil.checkStopDetention(modelRow, detentionItems.destination?.field, detentionItems.destination?.image as Image, detentionItems.destination?.color);
        DetentionUtil.checkStopDetention(modelRow, detentionItems.next_stop?.field, detentionItems.next_stop?.image as Image, detentionItems.next_stop?.color);

        if (!modelRow.isNull(detentionItems.statuses?.field)) {
            DetentionUtil.checkMultipleStopsDetention(modelRow, detentionItems.statuses.field, detentionItems.statuses.image as [Image, Image, Image], detentionItems.statuses.color);
        }
    }

    private static getDefaultDetentionItems(tableRow: TableRow): DetentionItems {
        return {
            origin: {
                image: tableRow.findComponentById("imageOriginDetention") as Image,
                field: "origin_detention",
            },
            destination: {
                image: tableRow.findComponentById("imageDestDetention") as Image,
                field: "destination_detention",
            },
            next_stop: {
                image: tableRow.findComponentById("imageNextStopDetention") as Image,
                field: "next_stop_detention",
            },
            statuses: {
                image: [
                    tableRow.findComponentById("imageDetentionActive") as Image,
                    tableRow.findComponentById("imageDetentionReview") as Image,
                    tableRow.findComponentById("imageDetentionClosed") as Image,
                ],
                field: "stop_detention_statuses"
            }
        }
    }

    static displayBillingImage(tableRow: TableRow) {
        if (!["D"].includes(tableRow.data.getString("move_status"))) return;
        const imageNotReadyToBill = tableRow.findComponentById("imageNotReadyToBill") as Image;
        const imageBilled = tableRow.findComponentById("imageBilled") as Image;
        if (imageNotReadyToBill != null) {
            imageNotReadyToBill.visible = !tableRow.data.getBoolean("ready_to_bill", false);
            return;
        }
        if (imageBilled != null) {
            imageBilled.visible = tableRow.data.get("bill_date") != null;
            imageBilled.tooltip = "Billed on " + tableRow.data.get("bill_date");
            return;
        }
    }

    static displayRateConImage(imageRateCon: Image, modelRow: ModelRow) {
        if (imageRateCon == null) return;
        const rateConStatus = modelRow.get("rate_con_status");
        const imageName = modelRow.get("rate_con_status_image_name");
        imageRateCon.visible = imageName != null;
        if (rateConStatus != null) {
            imageRateCon.name = imageName;
            imageRateCon.color = modelRow.get("rate_con_status_image_color", "default");
            CommonTooltips.setTooltipContentTextboxes(imageRateCon, () => [
                {caption: "Rate Con Status", text: modelRow.get("rate_con_status_desc")}
            ]);
            imageRateCon.tooltipDynamicOptions = {position: Alignment.RIGHT};
        }
    }

    static displayServiceFailureImage(tableRow: TableRow) {
        const imageServiceIncident = tableRow.findComponentById("imageServiceIncident") as Image;
        if (imageServiceIncident == null) return;
        imageServiceIncident.visible = !!tableRow.data.getBoolean("has_service_failures", false);
    }

    static displayLockImage(imageRateLock: Image, modelRow: ModelRow) {
        if (imageRateLock == null) return;
        const locked = modelRow.get("locked", false);
        imageRateLock.visible = locked;
        if (locked) {
            const waterfallInProgress = modelRow.get("waterfall_in_progress", false);
            imageRateLock.name = waterfallInProgress ? ImageName.waterfall : ImageName.lock;
            CommonTooltips.setTooltipContentLayout(imageRateLock, modelRow, "lme/powerbroker/MovementLockQuickInfo", {
                minWidth: 100,
                minHeight: 100
            });
            imageRateLock.tooltipDynamicOptions = { position: Alignment.RIGHT };
        }
    }

    static async displayLoadBoardImage(tableRow: TableRow) {
        const imageLoadboard = this.findImageComponent(tableRow, "imageLoadboard");
        const imageLoadboardError = this.findImageComponent(tableRow, "imagePostingLoadboardError");

        try {

            if (this.isPostedByMovement(tableRow)) {
                this.handlePostedByMovement(imageLoadboardError, imageLoadboard, tableRow);
            } else if (this.isPostedByDifferentMovement(tableRow)) {
                if (imageLoadboard) {
                    imageLoadboard.visible = false;
                }
            } else if (this.shouldShowLoadboard(tableRow)) {
                this.handleLoadboardImageVisibility(tableRow, imageLoadboard, imageLoadboardError);
            } else {
                this.hideImages(imageLoadboard, imageLoadboardError);
            }
        } catch (error) {
            console.error("API call failed:", error);
        }
    }

    static findImageComponent(tableRow, componentId) {
        return tableRow.findComponentById(componentId) as Image;
    }

    static isPostedByMovement(tableRow) {
        return tableRow.data.get("posted_by_movement") && tableRow.data.get("is_posted_movement");
    }

    static handlePostedByMovement(imageLoadboardError, imageLoadboard, tableRow) {
        const reasonsList = tableRow?.data?.get("loadboard_exception_list");
        if (reasonsList && Array.isArray(reasonsList) && reasonsList.length > 0) {
            if (imageLoadboardError) {
                this.handleLoadboardErrorTooltip(tableRow, reasonsList, imageLoadboardError);
                imageLoadboardError.visible = true;
            }
        } else {
            if (imageLoadboardError) {
                imageLoadboardError.visible = false;
            }
            if (imageLoadboard) {
                imageLoadboard.visible = true;
            }
            this.setUpLoadBoardPostingSuccessfulTooltip(tableRow, imageLoadboard, imageLoadboardError);
        }
    }

    static isPostedByDifferentMovement(tableRow) {
        return tableRow.data.get("posted_by_movement") && !tableRow.data.get("is_posted_movement");
    }

    static shouldShowLoadboard(tableRow) {
        return tableRow.data.getBoolean("loadboard") && !StringUtil.isEmptyString(tableRow.data.get("order_id"));
    }

    static handleLoadboardImageVisibility(tableRow, imageLoadboard, imageLoadboardError) {
        const reasonsList = tableRow?.data?.get("loadboard_exception_list");
        if (reasonsList && Array.isArray(reasonsList) && reasonsList.length > 0) {
            this.handleLoadboardErrorTooltip(tableRow, reasonsList, imageLoadboardError);
            if (imageLoadboardError) {
                imageLoadboardError.visible = true;
            }
        } else {
            this.setUpLoadBoardPostingSuccessfulTooltip(tableRow, imageLoadboard, imageLoadboardError);
        }
    }

    static handleLoadboardErrorTooltip(tableRow, reasonsList, imageLoadboardError) {
        const uniqueReasons = [...new Set(reasonsList)];
        const uniqueFormattedReasons = uniqueReasons.map(reason => `• ${reason}`).join("\n");
        if (uniqueFormattedReasons) {
            if (imageLoadboardError) {
                imageLoadboardError.visible = true;
                this.setUpLoadBoardErrorTooltip(tableRow, uniqueFormattedReasons, imageLoadboardError);
            }
        }
    }

    static hideImages(imageLoadboard, imageLoadboardError) {
        if (imageLoadboard) {
            imageLoadboard.visible = false;
        }
        if (imageLoadboardError) {
            imageLoadboardError.visible = false;
        }
    }

    static setUpLoadBoardErrorTooltip(tableRow: TableRow, message: string, imageLoadboardError: Image) {
        imageLoadboardError.tooltipDynamicContent = () => {
            const panel = new Panel({padding: 0, margin: 0, wrap: true, maxWidth: 500});
            panel.add(new Label({
                text: "Load Board Error",
                fontBold: true,
                color: "error",
                imageName: ImageName.triangleWarning,
                rowBreak: true
            }));
            panel.add(new Label({
                text: "There is an error posting to the Load Board. See below for the reasons:",
                fontBold: true,
                rowBreak: true,
                marginLeft: 24
            }));
            panel.add(new Label({
                text: message,
                marginLeft: 28,
                marginTop: 1,
                marginBottom: 1,
                padding: 0,
                rowBreak: true
            }));
            const pencil = new Image({
                name: ImageName.pencil,
                visible: true,
                height: 22,
                width: 22,
                marginLeft: 24,
                marginTop: 12,
                tooltip: "Edit load board posting",
                rowBreak: false
            });
            const cancel = new Image({
                name: ImageName.circleX,
                visible: true,
                height: 22,
                width: 22,
                marginTop: 12,
                tooltip: "Cancel load board posting",
                rowBreak: false
            });

            panel.add(pencil);
            if (!pencil.hasListeners(ComponentListenerDefs.click)) {
                pencil.addClickListener(() => this.openLoadBoardSlide(tableRow));
            }

            panel.add(cancel);
            cancel.addClickListener(() => {
                this.changeLoadboardFlag(tableRow);
                imageLoadboardError.visible = false;
            });

            return panel;
        };
    }

    static setUpLoadBoardPostingSuccessfulTooltip(tableRow: TableRow, imageLoadboard: Image, imageLoadboardError: Image) {
        if (imageLoadboardError) {
            imageLoadboardError.visible = false;
        }
        if (imageLoadboard) {
            imageLoadboard.visible = true;
            imageLoadboard.tooltipDynamicContent = () => {
                const panel = new Panel({padding: 0, margin: 0, wrap: true, maxWidth: 500});
                panel.add(new Label({
                    text: "Load Board Posting Successful",
                    fontBold: true,
                    color: "success",
                    imageName: ImageName.check,
                    rowBreak: true
                }));
                const pencil = new Image({
                    name: ImageName.pencil,
                    visible: true,
                    height: 22,
                    width: 22,
                    marginLeft: 5,
                    marginTop: 12,
                    tooltip: "Edit load board posting",
                    rowBreak: false
                });
                const cancel = new Image({
                    name: ImageName.circleX,
                    visible: true,
                    height: 22,
                    width: 22,
                    marginTop: 12,
                    tooltip: "Cancel load board posting",
                    rowBreak: false
                });

                panel.add(pencil);
                if (!pencil.hasListeners(ComponentListenerDefs.click)) {
                    pencil.addClickListener(() => this.openLoadBoardSlide(tableRow));
                }

                panel.add(cancel);
                cancel.addClickListener(() => {
                    this.changeLoadboardFlag(tableRow);
                    imageLoadboard.visible = false;
                });

                return panel;
            };
        }
    }

    static openLoadBoardSlide(tableRow: TableRow): void {
        const data = tableRow.data;
        const exceptionReasons = data?.get("loadboard_exception_list");
        const pbwLoadboardServices = data?.get("load_board_vendor_list");
        const orderId = data?.get("order_id");
        const origStopType = data?.get("origin_stop_type");
        const destStopType = data?.get("destination_stop_type");
        const movementId = data?.get("posted_by_movement") || (!data?.get("loadboard") && this.hasValidStopType(origStopType, destStopType)) ? data?.get("movement_id") : null;
        const originStopId = movementId === null ? data.get("order_origin_stop_id") : data.get("origin_stop_id");


        const origCity = data?.get("origin_city");
        const origState = data?.get("origin_state");
        const destCity = data?.get("destination_city");
        const destState = data?.get("destination_state");

        let postTitle = "Post to Load Boards - Order " + orderId;

        if (movementId !== null) {
            postTitle = postTitle + '\t' + StringUtil.toProperCase(origCity) + ', ' + origState + ' to ' + StringUtil.toProperCase(destCity) + ', ' + destState;
        }
        PostToLoadBoards.showInSlideoutDecorator(orderId, postTitle, originStopId, movementId, exceptionReasons, false, pbwLoadboardServices);
    }

    static changeLoadboardFlag(tableRow: TableRow) {
        const data = tableRow.data;
        const orderId = data?.get("order_id");
        PlanningUtil.updateLoadboardFlag(orderId);
    }

    static hasValidStopType(origStopType: string, destStopType: string) {
        return ('SP' === origStopType || 'SD' === origStopType) || ('SP' === destStopType || 'SD' === destStopType);
    }

    static displayDigitalOfferImage(tableRow: TableRow) {
        const imageDigitalOffer = tableRow.findComponentById("imageDigitalOffer") as Image;
        if (imageDigitalOffer == null) return;
        imageDigitalOffer.visible = !!tableRow.data.getBoolean("digitally_booked");
    }

    static displayPackageErrorImage(tableRow: TableRow) {
        const imagePackageError = tableRow.findComponentById("imagePackageError") as Image;
        if (imagePackageError == null) return;
        if (tableRow.data.getBoolean("has_shipment_error")) {
            imagePackageError.visible = true;
            CommonTooltips.setTooltipContentLayout(imagePackageError, null, "lme/datafusion/StatusErrorQuickInfo", null, {
                "order_id": tableRow.data.get("order_id"),
                "parent_tablename": "edistatus"
            });
            imagePackageError.tooltipDynamicOptions = { position: Alignment.BOTTOM };
        } else {
            imagePackageError.visible = false;
        }
    }

    static displayIntercompanyImage(tableRow: TableRow) {
        const intercompanyImage = tableRow.findComponentById("imageIntercompany") as Image;
        if (intercompanyImage == null) return;
        intercompanyImage.visible = tableRow.data.getBoolean("has_intercompany", false) && tableRow.data.getBoolean("licensed_for_ic", false);
    }

    static async updateLoadboardFlag(orderId: string) {
        const rowOrder = await new ModelOrders().searchSingle({id: orderId});
        rowOrder.set("loadboard", false);
        rowOrder.set("loadboard_post_movement_id", "");
        await rowOrder.post();
    }

    static displayBadges(tableRow: TableRow) {
        const badgeHold = tableRow.findComponentById("badgeHold") as Label;
        if (badgeHold != null) {
            CommonTooltips.setTooltipContentTextboxes(badgeHold, () => [{
                caption: "Reason",
                text: tableRow.data.get("hold_reason")
            }]);
            setRowComponentVisible(tableRow, badgeHold, badgeHold.field, true);
            if (badgeHold.visible)
                badgeHold.text = "HOLD";
        }
        const badgePending = tableRow.findComponentById("labelPendingBadge") as Label;
        if (badgePending != null) {
            this.setPendingBadge(tableRow, badgePending);
        }
        const badgeBrTrackStatus = tableRow.findComponentById("badgeBrTrackStatus") as Label;
        if (badgeBrTrackStatus != null) {
            setRowComponentVisible(tableRow, badgeBrTrackStatus, badgeBrTrackStatus.field);
        }
    }

    static setPendingBadge(tableRow: TableRow, comp: Label) {
        setRowComponentVisible(tableRow, comp, comp.sortField, "Y");
        if (comp.visible) {
            comp.text = "Pending";
            let tooltipData = null;
            if (tableRow.data.get("pending_carrier_assigned") == true) {
                tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                    movement_id: tableRow.data.get("movement_id"),
                    locked_user: tableRow.data.get("locked_user"),
                    name: tableRow.data.get("pending_carrier_name"),
                    phone_number: tableRow.data.get("pending_carrier_phone") ?? "",
                    city: tableRow.data.get("pending_carrier_city") ?? "",
                    state: tableRow.data.get("pending_carrier_state") ?? "",
                    zip_code: tableRow.data.get("pending_carrier_zip") ?? "",
                    icc_number: tableRow.data.get("pending_carrier_mc"),
                    dot_number: tableRow.data.get("pending_carrier_dot"),
                    intrastate_code: tableRow.data.get("pending_carrier_isc"),
                    pending_carrier: true,
                    id: tableRow.data.get("pending_payee_id"),
                    show_iso_icon: tableRow.data.get("show_iso_icon")
                })
                CommonTooltips.setTooltipContentLayout(comp, tooltipData, "lme/powerbroker/CarrierQuickInfo", {
                    minWidth: 100,
                    minHeight: 100
                });
                comp.tooltipDynamicOptions = { position: Alignment.RIGHT };
            } else {
                Api.search("lme/powerbroker/carriers-for-assignment", {id: tableRow.data.get("pending_payee_id")})
                    .then(result => {
                        const payeeData = result?.data[0];
                        if (payeeData) {
                            tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
                                movement_id: tableRow.data.get("movement_id"),
                                locked_user: tableRow.data.get("locked_user"),
                                name: payeeData.name,
                                phone_number: payeeData.phone_number ?? "",
                                icc_number: payeeData.icc_number,
                                dot_number: payeeData.dot_number,
                                intrastate_code: payeeData.intrastate_code,
                                pending_carrier: false,
                                id: tableRow.data.get("pending_payee_id")
                            });
                            CommonTooltips.setTooltipContentLayout(comp, tooltipData, "lme/powerbroker/CarrierQuickInfo", {
                                minWidth: 100,
                                minHeight: 100
                            });
                            comp.tooltipDynamicOptions = { position: Alignment.RIGHT };
                        }
                    });
            }
            CommonTooltips.setTooltipContentLayout(comp, tooltipData ?? tableRow.data.get("pending_payee_id"), "lme/powerbroker/CarrierQuickInfo", {
                minWidth: 100,
                minHeight: 100
            });
            comp.tooltipDynamicOptions = { position: Alignment.RIGHT };
        }
    }


    public static showDateRange(comp: Label | Textbox, startDay: string, daysOut: string) {
        comp.text = DateRange.formatWithDaysOut(startDay, daysOut);
        comp.visible = !comp.isEmpty();
    }

    static setupDriverStatus(tableRow: TableRow, modelRow: ModelRow, driver2: boolean = false) {
        const setLabels = (labelNewDriver: Label, labelTempStatus: Label, hireDateField: string) => {
            if (labelNewDriver) {
                this.displayNewDriverDays(labelNewDriver, modelRow.getDate(hireDateField));
            }
            if (labelTempStatus) {
                if (labelNewDriver.visible) {
                    labelTempStatus.setProps({ marginLeft: -7, paddingRight: 10, paddingLeft: 8})
                }
            }
        }
        const labelNewDriver = tableRow.findComponentById("labelNewDriver") as Label;
        const labelTempStatus = tableRow.findComponentById("labelTempStatus") as Label;
        setLabels(labelNewDriver, labelTempStatus, "driver1_hire_date");
        if (driver2) {
            const labelNewDriver = tableRow.findComponentById("labelNewDriver2") as Label;
            const labelTempStatus = tableRow.findComponentById("labelD2TempStatus") as Label;
            setLabels(labelNewDriver, labelTempStatus, "driver2_hire_date");
        }
    }

    public static setupNextStopLateStatusPanel(tableRow: TableRow) {
        const nextStopLateStatus = tableRow?.data?.get("next_stop_late_status");
        const panel = tableRow?.findComponentById("panelNextStopLateStatus") as Panel;
        if(panel) {
            if (nextStopLateStatus == null) {
                panel.visible = false;
                return;
            }
            panel.visible = true;
            this.setupLateStatus(panel, tableRow.data);
        }
    }

    public static setupLateStatus(panel: Panel, data: ModelRow) {
        const labelNextStopLateStatus = panel.findComponentById("labelNextStopLateStatus") as Label;
        const imageBrokerageStatus = panel.findComponentById("imageNextStopLateStatusChevron") as Image;
        const bgColor = data.get("next_stop_late_status_color");
        if (bgColor === "#ffffff") {
            panel.borderWidth = 1;
            panel.borderColor = "primary";
            panel.backgroundColor = bgColor;
            if (labelNextStopLateStatus) {
                labelNextStopLateStatus.color = "primary";
            }
            if (imageBrokerageStatus) {
                imageBrokerageStatus.color = "primary";
            }
        } else {
            panel.borderWidth = 0;
            panel.backgroundColor = bgColor;
            if (labelNextStopLateStatus) {
                labelNextStopLateStatus.color = "default.reverse";
            }
            if (imageBrokerageStatus) {
                imageBrokerageStatus.color = "default.reverse";
            }
        }
        if (labelNextStopLateStatus) {
            labelNextStopLateStatus.text = data.get("next_stop_late_status") || "--";
        }
    }

    static displayNewDriverDays(labelNewDriver: Label, hireDate: Date) {
        const dateDiff = DateUtil.dateDiff(DatePart.DAY, DateUtil.justDate(new Date()), DateUtil.justDate(hireDate));
        const newDriverDays1 = getDispatchControlValue("new_driver_days1");
        const newDriverDays2 = getDispatchControlValue("new_driver_days2");
        labelNewDriver.text = dateDiff >= 0 ? dateDiff <= newDriverDays1 ? newDriverDays1 : dateDiff <= newDriverDays2 ? newDriverDays2 : "" : "";
        labelNewDriver.tooltip = !StringUtil.isEmptyString(labelNewDriver.text) ? `The driver's hire date was ${dateDiff} days ago.` : "";
        labelNewDriver.visible = !StringUtil.isEmptyString(labelNewDriver.text);
    }

    public static getTempWarnings(modelRow: ModelRow): {return_air: TempWarnings|null, setpoint: TempWarnings|null} {
        const maxTemp = modelRow.getNumber("max_temp");
        const minTemp = modelRow.getNumber("min_temp");
        const returnTemp = modelRow.getNumber("return_temp");
        const setpointTemp = modelRow.getNumber("setpoint_temp");
        const comSetpointTemp = modelRow.getNumber("com_setpoint_temp");
        const returnVar = modelRow.getNumber("return_var", 0.0);
        const setpointVar = modelRow.getNumber("setpoint_var", 0.0);

        if (!maxTemp || !minTemp) {
            return {return_air: null, setpoint: null};
        }
        const result = {return_air: TempWarnings.WITHIN, setpoint: TempWarnings.WITHIN};
        if (!returnTemp) {
            result.return_air = TempWarnings.EXCEEDS;
        }
        if (setpointTemp && !comSetpointTemp) {
            result.setpoint = TempWarnings.EXCEEDS;
        }

        if (returnTemp > (returnVar + maxTemp) || returnTemp < (minTemp - returnVar)) {
            result.return_air = TempWarnings.EXCEEDS;
        } else if ((returnTemp >= (minTemp - returnVar) && returnTemp < minTemp)
            || (returnTemp <= (maxTemp + returnVar) && returnTemp > maxTemp)) {
            result.return_air = TempWarnings.APPROACHING;
        }

        if (comSetpointTemp > (setpointTemp + setpointVar) || comSetpointTemp < (setpointTemp - setpointVar)) {
            result.setpoint = TempWarnings.EXCEEDS;
        } else if ((comSetpointTemp >= (setpointTemp - setpointVar) && comSetpointTemp < setpointTemp)
            || (comSetpointTemp <= (setpointTemp + setpointVar) && comSetpointTemp > setpointTemp)) {
            result.setpoint = TempWarnings.APPROACHING;
        }

        return result;
    }

    public static calculateReeferTempStatus(modelRow: ModelRow) {
        const tempWarnings = PlanningUtil.getTempWarnings(modelRow);
        if (!tempWarnings || tempWarnings.return_air == null || tempWarnings.setpoint == null) {
            modelRow.set("reeferStatusInt", -1);
            return;
        }
        if (tempWarnings.return_air == TempWarnings.EXCEEDS || tempWarnings.setpoint == TempWarnings.EXCEEDS) {
            modelRow.set("reeferStatusInt", TempWarnings.EXCEEDS);
        } else if (tempWarnings.return_air == TempWarnings.APPROACHING || tempWarnings.setpoint == TempWarnings.APPROACHING) {
            modelRow.set("reeferStatusInt", TempWarnings.APPROACHING);
        } else if (tempWarnings.return_air == TempWarnings.WITHIN || tempWarnings.setpoint == TempWarnings.WITHIN) {
            modelRow.set("reeferStatusInt", TempWarnings.WITHIN);
        }
    }

    public static setReeferIcon(image: Image, modelRow: ModelRow) {
        if (image == null) return;

        const reeferId = modelRow.get("reefer_id");
        // Add the reefer id as a tooltip if there is no order
        // If there is an order, it will get a quick info window/tooltip
        if (modelRow.isNull("order_id") && reeferId != null) {
            image.tooltip = `Reefer number: ${reeferId}`;
        }

        if ("A" === modelRow.getString("move_status")) {
            image.visible = false;
            return;
        }

        const reeferStatus = modelRow.get("reeferStatusInt", -1);
        image.visible = true;
        switch (reeferStatus) {
            case TempWarnings.EXCEEDS:
                image.name = ImageName.reeferStatusError;
                image.color = null;
                break;
            case TempWarnings.APPROACHING:
                image.name = ImageName.reeferStatusWarn;
                image.color = null;
                break;
            case TempWarnings.WITHIN:
                image.name = ImageName.reeferStatus;
                image.color = "primary";
                break;
            default:
                image.visible = false;
                break;
        }
    }

    public static setEquipIcons(tableRow: TableRow, modelRow: ModelRow, enforceRegulatory: boolean, hasDriver2?: boolean) {
        hasDriver2 ??= !modelRow.isNull("driver2_id");
        if (enforceRegulatory) {
            const setDriverRegulatoryImages = (results: DriverRegulatoryValidationResults, image: Image, driver: number) => {
                const driverNum = driver.toString();
                this.setRegValidImage([
                    results.driverLicense,
                    results.driverPhysical
                ], image, ExpirationValid.ERROR);
                if (!image.visible) {
                    this.setRegValidImage([
                        results.driverLicense,
                        results.driverPhysical,
                        results.driverMVR
                    ], image, ExpirationValid.WARN);
                }
                if (image.visible) {
                    const equipWarningProps = {
                        isDriver: true,
                        driverNum: driverNum,
                        minWidth: 100,
                        minHeight: 100,
                    };
                    CommonTooltips.setTooltipContentLayout(image, modelRow, "lme/dispatch/planning/EquipWarningQuickInfo", equipWarningProps);
                }
            }
            // Driver 1
            const driverWarningImage = tableRow.findComponentById("imageDriverWarning") as Image;
            if (driverWarningImage) {
                driverWarningImage.visible = false;
                const driver1RegulatoryResults = this.driverRegulatoryValidations(modelRow, 1);
                setDriverRegulatoryImages(driver1RegulatoryResults, driverWarningImage, 1);
            }

            // Driver 2
            const driver2WarningImage = tableRow.findComponentById("imageDriver2Warning") as Image;
            if (hasDriver2 && driver2WarningImage) {
                const driver2RegulatoryResults = this.driverRegulatoryValidations(modelRow, 2);
                driver2WarningImage.visible = false;
                setDriverRegulatoryImages(driver2RegulatoryResults, driver2WarningImage, 2);
            }
        }


        // tractor / trailers / dollies
        const tractorTrailerRegulatoryResults = this.tractorTrailerRegulatoryValidations(modelRow);
        const tractorTrailerWarningImage = tableRow.findComponentById("imageTractorTrailerWarning") as Image;
        const trailer2WarningImage = tableRow.findComponentById("imageTrailer2Warning") as Image;
        const trailer3WarningImage = tableRow.findComponentById("imageTrailer3Warning") as Image;
        if (tractorTrailerWarningImage) {
            tractorTrailerWarningImage.visible = false;
            if (!this.setRegValidImage([
                tractorTrailerRegulatoryResults.tractorInspect,
                tractorTrailerRegulatoryResults.trailerInspect
            ], tractorTrailerWarningImage, ExpirationValid.ERROR)) {
                this.setRegValidImage([
                    tractorTrailerRegulatoryResults.tractorInspect,
                    tractorTrailerRegulatoryResults.trailerInspect
                ], tractorTrailerWarningImage, ExpirationValid.WARN);
            }
            if (tractorTrailerWarningImage.visible) {
                const equipWarningProps = {
                    isDriver: false,
                    minWidth: 100,
                    minHeight: 100,
                };
                CommonTooltips.setTooltipContentLayout(tractorTrailerWarningImage, modelRow, "lme/dispatch/planning/EquipWarningQuickInfo", equipWarningProps);
            }
        }

        if (trailer2WarningImage) {
            trailer2WarningImage.visible = false;
            if (!this.setRegValidImage([
                tractorTrailerRegulatoryResults.trailer2Inspect
            ], trailer2WarningImage, ExpirationValid.ERROR)) {
                this.setRegValidImage([
                    tractorTrailerRegulatoryResults.trailer2Inspect
                ], trailer2WarningImage, ExpirationValid.WARN);
            }
            if (trailer2WarningImage.visible) {
                const equipWarningProps = {
                    isDriver: false,
                    trailerNum: 2,
                    minWidth: 100,
                    minHeight: 100,
                };
                CommonTooltips.setTooltipContentLayout(trailer2WarningImage, modelRow, "lme/dispatch/planning/EquipWarningQuickInfo", equipWarningProps);
            }
        }

        if (trailer3WarningImage) {
            trailer3WarningImage.visible = false;
            if (!this.setRegValidImage([
                tractorTrailerRegulatoryResults.trailer3Inspect
            ], trailer3WarningImage, ExpirationValid.ERROR)) {
                this.setRegValidImage([
                    tractorTrailerRegulatoryResults.trailer3Inspect
                ], trailer3WarningImage, ExpirationValid.WARN);
            }
            if (trailer3WarningImage.visible) {
                const equipWarningProps = {
                    isDriver: false,
                    trailerNum: 3,
                    minWidth: 100,
                    minHeight: 100,
                };
                CommonTooltips.setTooltipContentLayout(trailer3WarningImage, modelRow, "lme/dispatch/planning/EquipWarningQuickInfo", equipWarningProps);
            }
        }
    }

    public static driverRegulatoryValidations(modelRow: ModelRow, driver: number): DriverRegulatoryValidationResults {
        const today = DateUtil.justDate(new Date());
        const results: DriverRegulatoryValidationResults = {};
        const driverWarnPeriod = 20;
        const driverNum = driver.toString();

        if (!modelRow.isNull(`driver${driverNum}_license_date`)) {
            const licenseDate = modelRow.getDate(`driver${driverNum}_license_date`);
            const licenseDiff = DateUtil.dateDiff(DatePart.DAY, licenseDate, today);
            const result = this.getRegulatoryValidation(licenseDiff, driverWarnPeriod);
            if (result) results.driverLicense = result;
        }

        if (!modelRow.isNull(`driver${driverNum}_physical_date`)) {
            const physicalDate = modelRow.getDate(`driver${driverNum}_physical_date`);
            const physicalDiff = DateUtil.dateDiff(DatePart.DAY, physicalDate, today);
            const result = this.getRegulatoryValidation(physicalDiff, driverWarnPeriod);
            if (result) results.driverPhysical = result;
        }

        if (!modelRow.isNull(`driver${driverNum}_mvr_date`)) {
            const mvrDate = modelRow.getDate(`driver${driverNum}_mvr_date`);
            const mvrDiff = DateUtil.dateDiff(DatePart.DAY, mvrDate, today);
            const result = this.getRegulatoryValidation(mvrDiff, driverWarnPeriod, true);
            if (result) results.driverMVR = result;
        }

        return results;
    }

    public static tractorTrailerRegulatoryValidations(modelRow: ModelRow): TractorTrailerRegulatoryValidationResults {
        const today = DateUtil.justDate(new Date());
        const results: TractorTrailerRegulatoryValidationResults = {};
        const tractorTrailerWarnPeriod = 30;

        const getResult = (field: string): RegulatoryValidation | null => {
            const inspectDate = modelRow.getDate(field);
            const inspectDiff = DateUtil.dateDiff(DatePart.DAY, inspectDate, DateUtil.dateAdd(DatePart.YEAR, today, -1));
            return this.getRegulatoryValidation(inspectDiff, tractorTrailerWarnPeriod);
        };

        if (!modelRow.isNull("tractor_last_inspection")) {
            const result = getResult("tractor_last_inspection");
            if (result) results.tractorInspect = result;
        }

        if (!modelRow.isNull("trailer_last_inspection")) {
            const result = getResult("trailer_last_inspection");
            if (result) results.trailerInspect = result;
        }

        if (!modelRow.isNull("trailer2_last_inspection")) {
            const result = getResult("trailer2_last_inspection");
            if (result) results.trailer2Inspect = result;
        }

        if (!modelRow.isNull("trailer3_last_inspection")) {
            const result = getResult("trailer3_last_inspection");
            if (result) results.trailer3Inspect = result;
        }

        return results;
    }

    private static getRegulatoryValidation(diff: number, warnPeriod: number, alwaysWarn=false): RegulatoryValidation | null {
        let result: RegulatoryValidation = null;
        if (diff < 0) {
            result = {
                valid: ExpirationValid.ERROR,
                diff,
            }
        } else if (diff <= warnPeriod) {
            result = {
                valid: ExpirationValid.WARN,
                diff,
            }
        }
        if (result && alwaysWarn) {
            result.alwaysWarn = true;
        }

        return result;
    }

    public static setRegValidImage(results: RegulatoryValidation[], image: Image, level: ExpirationValid): boolean {
        const isError = ExpirationValid.ERROR == level;
        if (results.some(item => (ExpirationValid.WARN == level && item?.alwaysWarn) || level == item?.valid)) {
            image.color = isError ? "error" : "warning";
            image.name = isError ? ImageName.circleErrorSolid : ImageName.circleError;
            image.visible = true;
            if (isError) return true;
        }
        return false;
    }

    public static displayEquipAlerts(tableRow: TableRow, modelRow: ModelRow, imageId: string, fieldMsgCount: string,
                                     fieldMsgUrgent: string, driverAlertProps?: DriverAlertsProps) {
        const alertImage: Image = tableRow.findComponentById(imageId) as Image;
        if (modelRow.getNumber(fieldMsgCount, 0) > 0) {
            alertImage.name = modelRow.getBoolean(fieldMsgUrgent) ? ImageName.equipmentAlertUrgent : ImageName.equipmentAlerts;
            alertImage.addClickListener((event: ClickEvent) => {
                DriverAlerts.openSlideout(driverAlertProps);
            })

            const equipIds = [];
            if (driverAlertProps.driverId)
                equipIds.push(driverAlertProps.driverId);
            if (driverAlertProps.tractorId)
                equipIds.push(driverAlertProps.tractorId);
            if (driverAlertProps.trailerId)
                equipIds.push(driverAlertProps.trailerId);
            PlanningUtil.setEquipAlertTooltipFromLayoutCallback(alertImage, modelRow, equipIds);

            alertImage.visible = true;
        } else {
            alertImage.visible = false;
        }
    }

    public static imageEquipAlertOnClick(event: ClickEvent) {
        const imageDriverAlert = event.target as Image;
        const tableRow = TableRow.getContainingTableRow(imageDriverAlert) as TableRow;
        DriverAlerts.openSlideout({
            trailerId: tableRow.data.get("trailer"),
            tractorId: tableRow.data.get("tractor"),
            driverId: tableRow.data.get("driver1_id")
        });
    }

    public static setEquipAlertTooltipFromLayoutCallback(component: Component, modelRow: ModelRow, equipIds: string[]) {
        component.tooltipDynamicContent = async () => {
            const layout = await Layout.getLoadedLayout("lme/dispatch/asset/DispatchEquipAlerts") as DispatchEquipAlerts;
            layout.maxWidth = 1000;
            const searchFilter = {units: equipIds};
            await layout.mainDataSource.search(searchFilter);
            const hideColumns = ["Status", "Message Date"];
            hideColumns.forEach(caption => {
                const column = layout.tableAlerts.getColumnByCaption(caption);
                if (column) {
                    layout.tableAlerts.setColumnVisibility(column, false);
                }
            });
            return layout;
        }
    }

    public static setDriverFuelCardsTooltipFromLayoutCallback(component: Component, modelRow: ModelRow) {
        if (modelRow.data.driver_has_active_fuel_card) {
            CommonTooltips.setTooltipContentLayout(
                component,
                null,
                "lme/dispatch/asset/DriverFuelCardQuickInfo",
                null,
                {
                    driver_id: modelRow.get("driver1_id"),
                    payee_id: modelRow.get("driver1_payee_id")
                });
        } else {
            component.visible = false;
            component.enabled = false;
        }
    }

    public static convertToHours(value: string): string {
        if (StringUtil.isEmptyString(value)) return value;

        const hosFormatSetting = getDispatchControlValue("driverhours_format");
        if ("D" === hosFormatSetting || StringUtil.isEmptyString(hosFormatSetting)) {
            return value;
        }

        const doubleValue = parseFloat(value);
        const hours = Math.floor(doubleValue);
        const minutes = Math.round((doubleValue - hours) * 60);

        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    }

    /**
     * Formats two dates as a date-time window
     * If both dates are on the same day and time, formats as just "datetime"
     * If same day but different times, formats as "datetime - time"
     * Otherwise formats as "datetime - datetime"
     *
     * @param startDate The start date and time
     * @param endDate The end date and time
     * @returns Formatted date-time window string
     */
    public static formatDateWindow(startDate: Date, endDate: Date): string {

        if (!startDate) return '';
        if (!endDate) return DateUtil.formatDateTime(startDate);

        // Make sure startDate is actually the earlier date
        if (endDate < startDate) {
            const temp = startDate;
            startDate = endDate;
            endDate = temp;
        }

        const exactMatch = startDate.toISOString() === endDate.toISOString();
        if (exactMatch) {
            // If times are exactly the same, just return one date-time
            return DateUtil.formatDateTime(startDate);
        }


        let formattedEnd: string;
        const sameDay = DateUtil.dateDiff(DatePart.DAY, startDate, endDate) === 0;
        if (sameDay) {
            // If same day, only show the time portion
            formattedEnd = DateUtil.formatTime(endDate);
        } else {
            // If different days, show full date-time
            formattedEnd = DateUtil.formatDateTime(endDate);
        }

        const formattedStart = DateUtil.formatDateTime(startDate);
        return `${formattedStart} - ${formattedEnd}`;
    }

    public static openICSlideout(props: IntercompanyProps){
        const { orderId, moveId, customerId } = props;
        if (orderId && moveId && customerId) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                width: window.innerWidth * .80,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;
            new SlideoutDecorator({
                layout: layout,
                title: "DataFusion - Order " + orderId,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                layoutLoadListeners: (event) => {
                    layout.sourceEdistatus.search({ order_id: orderId });
                    layout.sourceEdiOrder.search({ order_id: orderId, movement_id: moveId });
                    layout.sourceEdiMovementCarrier.search({ movement_id: moveId });
                    layout.sourceEdiCustomer.search({ customer_id: customerId, transaction_type: SHIPMENT_STATUS });
                    layout.sourceEdiMovementOrderIc.search({ order_id: orderId, original_order: "Y", in_movement_id: moveId});
                },
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    let selIndex: number = 0;
                    layout.saveButton.addClickListener(event => {
                        if (decorator.validateSimple()) {
                            const dataLayout = layout.outboundDataSource.data;
                            dataLayout?.forEach(async (data, index) => {
                                if (data.hasChanged()) {
                                    await layout.outboundDataSource.data[index].post();
                                }
                            });
                        }
                    });
                    selIndex = layout.tabsetDataFusion.indexOf(layout.tabIntercompany);
                    layout.tabsetDataFusion.selectedIndex = selIndex;
                },
            });
            layout.orderId = orderId;
            layout.movementId = moveId;
            layout.customerId = customerId;
        }
    }

    public static setupYsplitIcons(tableRow: TableRow, modelRow: ModelRow) {
        const originStopType = modelRow.get("origin_stop_type", "");
        const destStopType = modelRow.get("destination_stop_type", "");
        const imageYsplitComplete = tableRow.findComponentById("imageYsplitComplete") as Image;
        const imageYsplitPick = tableRow.findComponentById("imageYsplitPick") as Image;
        const imageYsplit = tableRow.findComponentById("imageYsplit") as Image;

        if (originStopType === "SP" && destStopType === "SD") {
            if (imageYsplitComplete) imageYsplitComplete.visible = true;
            if (imageYsplitPick) imageYsplitPick.visible = false;
            if (imageYsplit) imageYsplit.visible = false;
            return;
        }

        if (imageYsplitComplete) imageYsplitComplete.visible = false;
        if (imageYsplitPick) imageYsplitPick.visible = originStopType === "SP";
        if (imageYsplit) imageYsplit.visible = destStopType === "SD";
    }

    public static setUpMcMessages(tableRow: TableRow, modelRow: ModelRow) {
        const messageCount = modelRow.getNumber("mc_message_count", 0);
        const errorsCount = modelRow.getNumber("mc_message_errors", 0);
        const buttonMcMessages = tableRow.findComponentById("buttonMcMessages") as Button;
        const buttonMcUnread= tableRow.findComponentById("buttonMcUnread") as Button;
        const buttonMcErrors = tableRow.findComponentById("buttonMcErrors") as Button;
        buttonMcErrors.visible = errorsCount > 0;
        buttonMcUnread.visible = messageCount > 0 && !buttonMcErrors.visible;
        buttonMcMessages.visible = !buttonMcErrors.visible && !buttonMcUnread.visible;
    }

    public static showHideTrailerColumns(table: Table) {
        const identifier2 = "columnHeaderTrailer2";
        const identifier3 = "columnHeaderTrailer3";
        const dcMaxTrailers = getDispatchControlValue("maximum_trailers");
        if (dcMaxTrailers && !isNaN(dcMaxTrailers) && dcMaxTrailers < 3) {
            table.removeColumn(identifier3);
            if (dcMaxTrailers < 2)
                table.removeColumn(identifier2);
        }
    }

    public static determineOrderNumberVisibility(tableRow: TableRow, modelRow: ModelRow) {
        const panelIds = tableRow.findComponentById("panelIds") as Panel;
        const panelEmptyOrder = tableRow.findComponentById("panelEmptyOrder") as Panel;
        const emptied = modelRow.getBoolean("emptied", false);
        panelIds?.setProps({ visible: !emptied });
        panelEmptyOrder?.setProps({ visible: emptied });
    }

    public static async labelNextStopLateStatusChevronOnClick(event: ClickEvent, panel: Panel, stopId: string, movementId: string) {
        const dropDownItems: Partial<LabelProps>[] = [];
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = tableRow.data as ModelRow;
        Api.search("lme/dispatch/asset/late-route-status", {
            is_active: "Y",
            _field_list: {layoutName: "lme/dispatch/asset/LateRouteStatus" }
         }).then((result) => {
            if (result && result.actual_row_count > 0) {
                result.data.forEach(data => {
                    const row = new ModelRow("lme/dispatch/asset/late-route-status", false, data)
                    const labelProps: Partial<LabelProps> = {
                        text: row.get("descr", "id"),
                        onClick: () => PlanningUtil.updateLateRouteStatus(panel, movementId, stopId, row)
                    }
                    dropDownItems.push(labelProps);
                });
            }
         }).then(() => {
            panel["statusDropDownItems"] = dropDownItems;
            PlanningUtil.displayLateRouteStatusDropDowns(panel);
         }).catch(error => {
            log.error("Late Route Status fetch failed: %o", error);
         });
    }

    public static displayLateRouteStatusDropDowns(panel: Panel) {
        const dropDownItems = panel["statusDropDownItems"];
        if (dropDownItems != null) {
            Overlay.showDropdown(panel, dropDownItems, null, { width: 225 }, {
                align: Alignment.LEFT,
                onClose: () => {
                    panel.borderShadow = false;
                }
            });
        }
    }

    public static async updateLateRouteStatus(panel: Panel, movementId: string, stopId: string, statusRow: ModelRow) {
        const response = await Api.search("lme/dispatch/planning/late-route", {
            "movement_id": movementId,
            "next_stop_id": stopId,
        });
        if (response && response.actual_row_count > 0) {
            const modelRows: ModelRow[] = response.data.map(data => new ModelRow("lme/dispatch/planning/late-route", false, data));
            const lateRouteRow = modelRows.find(row => row.getBoolean("is_current")) || modelRows[0];
            const currentStatusId = lateRouteRow.get("late_route_status");
            const newStatusId = statusRow.get("id");
            const imageBrokerageStatus = panel.findComponentById("imageNextStopLateStatusChevron") as Image;
            if (currentStatusId !== newStatusId) {
                lateRouteRow.set("late_route_status", newStatusId);
                try {
                    if (imageBrokerageStatus != null)
                        imageBrokerageStatus.busy = true;
                    await Api.update("lme/dispatch/planning/late-route", lateRouteRow);
                } catch (error) {
                    log.error("Failed to update late route status:", error);
                    CommonDialogs.showError("Error updating late route status: " + (error?.message || error));
                }
                finally {
                    imageBrokerageStatus.busy = false;
                }
            }
        }
        else if (response && response.actual_row_count === 0) {
            CommonDialogs.showError("No existing late route record found to update.", "Update Failed");
        }

    }

    public static setDroppedTrailerFont(tableRow: TableRow, modelRow: ModelRow<any>, dcMaxTrailers: number = 1) {
        const trailerDropped = modelRow.get("trailer_dropped", false);

        const updateLabelStyle = (labelId: string, isDropped: boolean) => {
            const label = tableRow.findComponentById(labelId) as Label;
            if (label) {
                label.fontBold = isDropped;
                label.style.fontStyle = isDropped ? "italic" : "normal";
            }
        };

        updateLabelStyle("labelEquipTrailer", trailerDropped);
        updateLabelStyle("labelTrailer", trailerDropped);

        if (dcMaxTrailers > 1) {
            const trailer2Dropped = modelRow.get("trailer2_dropped", false);
            updateLabelStyle("labelEquipTrailer2", trailer2Dropped);
        }

        if (dcMaxTrailers > 2) {
            const trailer3Dropped = modelRow.get("trailer3_dropped", false);
            updateLabelStyle("labelEquipTrailer3", trailer3Dropped);
        }
    }
}
